/* eslint-disable max-lines-per-function */
import React from 'react'
import { Downgraded, type State } from '@hookstate/core'
import { Grid, Typography } from '@mui/material'
import ColumnChartHorizontal from '~/components/ColumnChartMui/HorizontalChart'
import ColumnChartVertical from '~/components/ColumnChartMui/VerticalChart'
import PieChartMui from '~/components/PieChartMui'
import { useLang } from '~/hooks/useLang'
import useManagementQualifiersInvolvedState from '~/features/Management/stores/ManagementQualifiersInvolvedState'
import { addTotals, calculateBarChartTotal } from '~/utils/format'
interface ChartsInvolvedProps {
  readonly expandFilterSearch: boolean
}

export default function ChartsInvolved({
  expandFilterSearch,
}: ChartsInvolvedProps) {
  const { translate } = useLang()
  const { occurrencesAssociatedPeople } = useManagementQualifiersInvolvedState()


  return (
    <>
      <Grid
        container
        xs={12}
        sx={{
          display: 'flex',
          paddingX: 3,
          paddingTop: 3,
          justifyContent: 'space-between',
        }}
      >
        <Grid item xs={12} hidden={!expandFilterSearch} paddingBottom={1}>
          <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
            {translate('Registered people')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          hidden={!expandFilterSearch}
          paddingRight={1}
          paddingBottom={1}
        >
          <ColumnChartHorizontal
            data={occurrencesAssociatedPeople()
              .occurrencesAssociatedPeopleByInvolvementType.attach(Downgraded)
              .get()}
            title={`${translate('Involvement type')}`}
            subtitle={`Total de envolvidos: ${calculateBarChartTotal(
              occurrencesAssociatedPeople()
                .occurrencesAssociatedPeopleByInvolvementType.attach(Downgraded)
                .get()
            )}`}
            typeTotal={'typeMinus'}
            colorBar={'primary'}
          />
        </Grid>
        <Grid
          item
          xs={6}
          hidden={!expandFilterSearch}
          paddingRight={1}
          paddingBottom={1}
        >
          <ColumnChartHorizontal
            data={occurrencesAssociatedPeople()
              .occurrencesAssociatedPeopleByOccurrenceType.attach(Downgraded)
              .get()}
            title={`${translate(
              'Total number of involved registrations X Type of occurrence'
            )}`}
            subtitle={`Total de envolvidos: ${calculateBarChartTotal(
              occurrencesAssociatedPeople()
                .occurrencesAssociatedPeopleByOccurrenceType.attach(Downgraded)
                .get()
            )}`}
            typeTotal={'typeMinus'}
            colorBar={'secondary'}
          />
        </Grid>
        <Grid
          item
          xs={6}
          hidden={!expandFilterSearch}
          paddingRight={1}
          paddingBottom={1}
        >
          <PieChartMui
            data={occurrencesAssociatedPeople()
              .occurrencesAssociatedPeopleByPublicAgent.attach(Downgraded)
              .get()}
            title={'Was the person involved a public servant?'}
            subtitle={`Total de envolvidos: ${addTotals(
              occurrencesAssociatedPeople()
                .occurrencesAssociatedPeopleByPublicAgent.attach(Downgraded)
                .get()
            )}`}
            typeTotal={'statusMinus'}
            showPercent
          />
        </Grid>
        <Grid
          item
          xs={6}
          hidden={!expandFilterSearch}
          paddingRight={1}
          paddingBottom={1}
        >
          <ColumnChartHorizontal
            data={occurrencesAssociatedPeople()
              .occurrencesAssociatedPeopleByOrganization.attach(Downgraded)
              .get()}
            title={`${translate('Organization of those involved')}`}
            subtitle={`Total de envolvidos: ${calculateBarChartTotal(
              occurrencesAssociatedPeople()
                .occurrencesAssociatedPeopleByOrganization.attach(Downgraded)
                .get()
            )}`}
            typeTotal={'organizations'}
            colorBar={'tertiary'}
          />
        </Grid>
        <Grid
          item
          xs={6}
          hidden={!expandFilterSearch}
          paddingRight={1}
          paddingBottom={1}
        >
          <ColumnChartHorizontal
            data={occurrencesAssociatedPeople()
              .occurrencesAssociatedPeopleByCircunstance.attach(Downgraded)
              .get()}
            title={`${translate('Circunstance of the person involved')}`}
            subtitle={`Total de envolvidos: ${calculateBarChartTotal(
              occurrencesAssociatedPeople()
                .occurrencesAssociatedPeopleByCircunstance.attach(Downgraded)
                .get()
            )}`}
            colorBar={'primary'}
          />
        </Grid>
        <Grid
          item
          xs={6}
          hidden={!expandFilterSearch}
          paddingRight={1}
          paddingBottom={1}
        >
          <ColumnChartHorizontal
            data={occurrencesAssociatedPeople()
              .occurrencesAssociatedPeopleByDestination.attach(Downgraded)
              .get()}
            title={`${translate('Final destination of the person involved')}`}
            subtitle={`Total de envolvidos: ${calculateBarChartTotal(
              occurrencesAssociatedPeople()
                .occurrencesAssociatedPeopleByDestination.attach(Downgraded)
                .get()
            )}`}
            typeTotal={'final destiny'}
            colorBar={'secondary'}
          />
        </Grid>
        <Grid
          item
          xs={6}
          hidden={!expandFilterSearch}
          paddingRight={1}
          paddingBottom={1}
        >
          <PieChartMui
            data={occurrencesAssociatedPeople()
              .occurrencesAssociatedPeopleArrested.attach(Downgraded)
              .get()}
            title={'Was there arrest?'}
            subtitle={`Total de prisões: ${addTotals(
              occurrencesAssociatedPeople()
                .occurrencesAssociatedPeopleArrested.attach(Downgraded)
                .get()
            )}`}
            typeTotal={'statusMinus'}
            showPercent
          />
        </Grid>
      </Grid>
      <Grid
        container
        xs={12}
        sx={{
          display: 'flex',
          paddingX: 3,
          paddingTop: 3,
          justifyContent: 'space-between',
        }}
      >
        <Grid item xs={12} hidden={!expandFilterSearch} paddingBottom={1}>
          <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>
            {translate('Personal Data')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          hidden={!expandFilterSearch}
          paddingRight={1}
          paddingBottom={1}
        >
          <PieChartMui
            data={occurrencesAssociatedPeople()
              .occurrencesAssociatedPeopleByGender.attach(Downgraded)
              .get()}
            title={'Gender of the person involved'}
            subtitle={`Total de envolvidos: ${addTotals(
              occurrencesAssociatedPeople()
                .occurrencesAssociatedPeopleByGender.attach(Downgraded)
                .get()
            )}`}
            typeTotal={'genders'}
            showPercent
          />
        </Grid>
        <Grid
          item
          xs={6}
          hidden={!expandFilterSearch}
          paddingRight={1}
          paddingBottom={1}
        >
          <ColumnChartHorizontal
            data={occurrencesAssociatedPeople()
              .occurrencesAssociatedPeopleByOccupation.attach(Downgraded)
              .get()}
            title={`${translate('Occupation of the Involved')}`}
            subtitle={`Total de envolvidos: ${calculateBarChartTotal(
              occurrencesAssociatedPeople()
                .occurrencesAssociatedPeopleByOccupation.attach(Downgraded)
                .get()
            )}`}
            typeTotal={'occupations'}
            colorBar={'secondary'}
          />
        </Grid>
        <Grid
          item
          xs={6}
          hidden={!expandFilterSearch}
          paddingRight={1}
          paddingBottom={1}
        >
          <ColumnChartVertical
            data={occurrencesAssociatedPeople()
              .occurrencesAssociatedPeopleByAge.attach(Downgraded)
              .get()}
            title={'Age of the person involved'}
            subtitle={`Total de envolvidos: ${calculateBarChartTotal(
              occurrencesAssociatedPeople()
                .occurrencesAssociatedPeopleByAge.attach(Downgraded)
                .get()
            )}`}
            typeTotal={'Ages'}
            colorBar="tertiary"
          />
        </Grid>
      </Grid>
    </>
  )
}
